import { useState, useMemo, useEffect } from 'react';
import { List, useListContext, CreateButton, TopToolbar } from 'react-admin';
import { Box, Card, CardContent, Typography, Chip, Stack, TextField, InputAdornment } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { card, GOLD, layout, inputSx, typo, clickableA11y } from '../styles/ds';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

const ListActions = () => (
  <TopToolbar>
    <CreateButton label="מוצר חדש" />
  </TopToolbar>
);

const ProductCards = () => {
  const { data = [], isLoading } = useListContext();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 12, page });

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.trim().toLowerCase();
    return data.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // A new search narrows/widens the set — always land back on page 1
  // rather than a now-meaningless page number from the previous list.
  useEffect(() => { setPage(1); }, [search]);

  const pageItems = useMemo(() => {
    return filtered.slice(startIndex, startIndex + renderCount);
  }, [filtered, startIndex, renderCount]);

  if (isLoading) return <Box className="crm-page" p={3}><Typography>טוען...</Typography></Box>;

  return (
    <Box>
      <Box className="crm-list-search" px={2} pt={2}>
        <TextField size="small" placeholder="חיפוש קטגוריה..." aria-label="חיפוש קטגוריות מוצר"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>, sx: { ...inputSx, direction: 'rtl' } }}
          sx={{ width: 280, mb: 2 }} />
      </Box>
      {filtered.length === 0 && (
        <Box sx={{ p: 4, mx: 2, mb: 2, textAlign: 'center', borderRadius: 2,
          border: '1.5px dashed rgba(13,29,62,0.18)', backgroundColor: 'rgba(13,29,62,0.015)' }}>
          <Typography color="text.secondary">{search ? 'לא נמצאו תוצאות' : 'אין מוצרים עדיין'}</Typography>
        </Box>
      )}
      <Box className="crm-form-grid crm-card-grid" ref={containerRef} sx={{ ...layout.cardGrid, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', minHeight: 200 }}>
      {pageItems.map((product, i) => (
        <Card key={product.id} onClick={() => navigate(`/products/${product.id}/show`)}
          {...clickableA11y(() => navigate(`/products/${product.id}/show`), { ariaLabel: `פתיחת קטגוריית מוצר ${product.name || ''}`, asButton: true })}
          sx={{ ...card.clickable, border: '1px solid rgba(13,29,62,0.06)' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" gap={1}>
              <InventoryIcon sx={{ color: GOLD, fontSize: 20 }} />
              <Typography fontWeight={700} fontSize={16} color="primary.main">
                {product.name}
              </Typography>
            </Stack>

            {product.description && (
              <Typography variant="body2" color="text.secondary" mt={1} sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {product.description}
              </Typography>
            )}

            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <Chip label={`# ${product.id}`} size="small"
                sx={{ backgroundColor: 'rgba(13,29,62,0.07)', fontSize: typo.meta.fontSize }} />
              <Chip label={`${product.variant_count ?? 0} מוצרים`} size="small"
                sx={{ backgroundColor: 'rgba(200,169,110,0.15)', color: '#7a6030', fontSize: typo.meta.fontSize }} />
            </Box>
          </CardContent>
        </Card>
      ))}
      </Box>
      <FitPagination page={page} setPage={setPage} total={filtered.length} perPage={fitCount} />
    </Box>
  );
};

const ProductList = () => (
  <List className="crm-mobile-list crm-inline-list" actions={<ListActions />} title="מוצרים" perPage={1000} pagination={false} storeKey={false}>
    <ProductCards />
  </List>
);

export default ProductList;
